import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { calculateTotals, getFormActionButtons, getFormTitle, transformFormData } from '../../../utils';
import { buildTaxRowsFromDetailRows, getTaxRowIdentity } from './build-tax-rows-from-detail-rows';
import { useChiPhiChiTietRows, useChiPhiRows, useDetailRows, useThueRows } from '.';
import { useHoaDonMuaHangTrongNuocSearchContext } from '../../../context';
import { formSchema, initialFormValues } from '../../../schema';
import { usePriceLookup } from '../../../hooks/usePriceLookup';
import { getDocumentsColumns } from '../get-documents-columns';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import { useFormFieldState } from '../../../hooks';
import { useAuth } from '@/contexts/auth-context';
import { roundNumber } from '@/lib/formatUtils';
import type { FormDialogProps } from '../types';

export const useFormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [taxConfirm, setTaxConfirm] = useState<{ open: boolean; maThueUuid?: string; newTax?: number }>({
    open: false
  });
  const originalTaxValuesRef = useRef<Record<string, number>>({});
  const [lastDetailAllocationMode, setLastDetailAllocationMode] = useState<'quantity' | 'value' | null>(null);
  const lastEditedDetailFieldRef = useRef<string | null>(null);

  const formState = useFormFieldState(initialData);
  const { state, actions } = formState;
  const previousCustomerUUUIDRef = useRef(state.khachHang?.uuid);
  const { transactionOptions } = useHoaDonMuaHangTrongNuocSearchContext();
  const { entityUnit } = useAuth();
  const constraints = useMemo(
    () => state.taiKhoan?.constraints ?? state.khachHang?.account_data?.constraints ?? [],
    [state.khachHang?.account_data?.constraints, state.taiKhoan?.constraints]
  );
  const schema = useMemo(() => formSchema(constraints), [constraints]);

  const {
    rows: detailRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange,
    setRows: setDetailRows,
    setNonWritableFields,
    setPasteConfigs,
    setPriceLookupHandler,
    isPasting
  } = useDetailRows(initialData?.chi_tiet_hoa_don || [], state.loai_ck, state.ck_tl_nt);

  const priceLookupHandler = usePriceLookup(state.khachHang?.uuid);
  useEffect(() => {
    setPriceLookupHandler(priceLookupHandler);
  }, [priceLookupHandler, setPriceLookupHandler]);

  useEffect(() => {
    if (!detailRows.length || previousCustomerUUUIDRef.current === state.khachHang?.uuid) return;
    previousCustomerUUUIDRef.current = state.khachHang?.uuid;

    const updateRow = (uuid: string, fields: Record<string, any>) => {
      setDetailRows(previousRows => previousRows.map(row => (row.uuid === uuid ? { ...row, ...fields } : row)));
    };

    detailRows.forEach(row => {
      if (!row.uuid) return;
      priceLookupHandler(row, 'ma_vt_data', row.ma_vt_data, updateRow, { isInitializing: false });
    });
  }, [detailRows, priceLookupHandler, setDetailRows, state.khachHang?.uuid]);

  const {
    rows: chiPhiRows,
    selectedRowUuid: chiPhiSelectedRowUuid,
    selectedCell: chiPhiSelectedCell,
    handleRowClick: handleChiPhiRowClick,
    handleCellClick: handleChiPhiCellClick,
    handleAddRow: handleAddChiPhiRow,
    handleDeleteRow: handleDeleteChiPhiRow,
    handleCopyRow: handleCopyChiPhiRow,
    handlePasteRow: handlePasteChiPhiRow,
    handleMoveRow: handleMoveChiPhiRow,
    handleCellValueChange: handleChiPhiCellValueChange,
    setNonWritableFields: setChiPhiNonWritableFields,
    setPasteConfigs: setChiPhiPasteConfigs,
    isPasting: chiPhiIsPasting
  } = useChiPhiRows(initialData?.chi_phi_hoa_don || []);

  const {
    rows: chiPhiChiTietRows,
    selectedRowUuid: chiPhiChiTietSelectedRowUuid,
    handleRowClick: handleChiPhiChiTietRowClick,
    handleAddRow: handleAddChiPhiChiTietRow,
    handleDeleteRow: handleDeleteChiPhiChiTietRow,
    handleCopyRow: handleCopyChiPhiChiTietRow,
    handlePasteRow: handlePasteChiPhiChiTietRow,
    handleMoveRow: handleMoveChiPhiChiTietRow,
    handleCellValueChange: handleChiPhiChiTietCellValueChange
  } = useChiPhiChiTietRows(initialData?.chi_phi_chi_tiet_hoa_don || [], chiPhiRows, detailRows);

  const {
    rows: thueRows,
    selectedRowUuid: thueSelectedRowUuid,
    selectedCell: thueSelectedCell,
    handleRowClick: handleThueRowClick,
    handleCellClick: handleThueCellClick,
    handleAddRow: handleAddThueRow,
    handleDeleteRow: handleDeleteThueRow,
    handleCopyRow: handleCopyThueRow,
    handlePasteRow: handlePasteThueRow,
    handleMoveRow: handleMoveThueRow,
    handleCellValueChange: handleThueCellValueChange,
    setRows: setThueRows,
    setNonWritableFields: setThueNonWritableFields,
    setPasteConfigs: setThuePasteConfigs,
    isPasting: thueIsPasting
  } = useThueRows(initialData?.thue_hoa_don || []);

  useEffect(() => {
    if (!open) {
      setActiveTab('info');
    }
  }, [open]);

  const recalculateTaxAfterRepeat = useCallback(
    (updatedRows: MuaHangRecord[], repeatedField: string, sourceRow: MuaHangRecord) => {
      const repeatedBaseField = repeatedField.endsWith('_data') ? repeatedField.replace(/_data$/, '') : repeatedField;

      if (repeatedBaseField !== 'ma_thue') {
        return updatedRows;
      }

      const sourceIndex = updatedRows.findIndex(row => row?.uuid === sourceRow?.uuid);

      return updatedRows.map((row, index) => {
        if (index <= sourceIndex) return row;

        const taxRate = Number(row?.ma_thue_data?.thue_suat ?? row?.ma_thue_data?.tax_rate ?? 0);
        const taxBaseValue = row?.tien_nt0 ?? Number(row?.gia_nt0 || 0) * Number(row?.so_luong || 0);
        const taxBase = Number(taxBaseValue || 0);

        return {
          ...row,
          thue_nt: Math.round(((taxBase * taxRate) / 100) * 100) / 100
        };
      });
    },
    []
  );

  const repeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows,
    markDirty: () => undefined,
    onAfterRepeat: recalculateTaxAfterRepeat
  });

  useEffect(() => {
    if (!detailRows || detailRows.length === 0) return;

    const costRowsByQuantity = (chiPhiRows || []).filter((r: MuaHangRecord) => String(r?.ma_cp_data?.loai_pb) === '1');
    const costRowsByValue = (chiPhiRows || []).filter((r: MuaHangRecord) => String(r?.ma_cp_data?.loai_pb) === '4');

    const totalAllocatedCostByQuantity = costRowsByQuantity.reduce(
      (sum: number, r: MuaHangRecord) => sum + Number(r?.tien_cp_nt || 0),
      0
    );
    const totalAllocatedCostByValue = costRowsByValue.reduce(
      (sum: number, r: MuaHangRecord) => sum + Number(r?.tien_cp_nt || 0),
      0
    );

    const updated = detailRows.map((row: MuaHangRecord) => {
      let newCp = 0;

      if (totalAllocatedCostByQuantity > 0) {
        const totalQuantity = detailRows.reduce((sum: number, r: MuaHangRecord) => sum + Number(r?.so_luong || 0), 0);
        if (Number.isFinite(totalQuantity) && totalQuantity > 0) {
          const ratePerUnit = totalAllocatedCostByQuantity / totalQuantity;
          const quantity = Number(row?.so_luong || 0);
          newCp += Math.round(ratePerUnit * quantity * 100) / 100;
        }
      }

      if (totalAllocatedCostByValue > 0) {
        const totalBaseAmount = detailRows.reduce((sum: number, r: MuaHangRecord) => {
          const baseAmount = state.ck_yn ? Number(r?.tien_tck_nt || 0) : Number(r?.tien_nt0 || 0);
          return sum + baseAmount;
        }, 0);

        if (Number.isFinite(totalBaseAmount) && totalBaseAmount > 0) {
          const ratePerAmount = totalAllocatedCostByValue / totalBaseAmount;
          const baseAmount = state.ck_yn ? Number(row?.tien_tck_nt || 0) : Number(row?.tien_nt0 || 0);
          newCp += Math.round(ratePerAmount * baseAmount * 100) / 100;
        }
      }

      const tien_nt0 = Number(row?.tien_nt0 || 0);
      const so_luong = Number(row?.so_luong || 0);
      const gia_nt = so_luong > 0 ? Math.round(((tien_nt0 + newCp) / so_luong) * 100) / 100 : 0;
      const currentCp = Number(row?.cp_nt || 0);
      const currentGiaNt = Number(row?.gia_nt || 0);

      if (currentCp === newCp && currentGiaNt === gia_nt) return row;

      return { ...row, cp_nt: newCp, gia_nt };
    });

    const changed = updated.some((row: MuaHangRecord, idx: number) => row !== detailRows[idx]);
    if (changed) setDetailRows(updated);
  }, [chiPhiRows, detailRows, setDetailRows, state.ck_yn]);

  const totals = useMemo(() => calculateTotals(detailRows, chiPhiRows), [detailRows, chiPhiRows]);

  const normalizeSubmitData = useCallback(
    (data: MuaHangRecord) => {
      const defaultMaGD =
        formMode === 'add'
          ? transactionOptions[0]?.value || initialFormValues.ma_gd || 'HD'
          : initialData?.ma_gd ||
            initialData?.ma_gd_data?.uuid ||
            transactionOptions[0]?.value ||
            initialFormValues.ma_gd ||
            'HD';

      return {
        ...initialFormValues,
        ...data,
        ma_gd: data.ma_gd || defaultMaGD
      };
    },
    [formMode, initialData?.ma_gd, initialData?.ma_gd_data?.uuid, transactionOptions]
  );

  const submit = useCallback(
    (data: MuaHangRecord) => {
      const submitData = normalizeSubmitData(data);
      const formData = transformFormData(
        submitData,
        state,
        formMode,
        entityUnit,
        detailRows,
        chiPhiRows,
        chiPhiChiTietRows,
        thueRows,
        totals,
        initialData?.chi_tiet_hoa_don
      );

      if (formMode === 'edit') {
        onSubmit?.({
          data: {
            ...formData,
            phieu_chi_uuids: initialData?.phieu_chi_uuids,
            phieu_nhap_kho_uuid: initialData?.phieu_nhap_kho_uuid
          },
          detailRows
        });
        return;
      }

      onSubmit?.({ data: formData, detailRows });
    },
    [
      chiPhiChiTietRows,
      chiPhiRows,
      detailRows,
      entityUnit,
      formMode,
      initialData?.chi_tiet_hoa_don,
      initialData?.phieu_chi_uuids,
      initialData?.phieu_nhap_kho_uuid,
      normalizeSubmitData,
      onSubmit,
      state,
      thueRows,
      totals
    ]
  );

  const allocateDetailTax = useCallback(
    (maThueUuid: string, totalTax: number) => {
      const targets = detailRows.filter(r => (r?.ma_thue_data?.uuid || '') === maThueUuid);
      if (targets.length === 0) return;

      const taxRow = thueRows.find((r: MuaHangRecord) => (r?.ma_thue_data?.uuid || '') === maThueUuid);
      const totalTienNt = Number((taxRow as MuaHangRecord).t_tien_nt || 0);

      if (totalTienNt <= 0) return;

      let remainder = Number(totalTax || 0);
      const updated = detailRows.map(r => ({ ...r }));

      targets.forEach((row, idx) => {
        const rowTienNt = Number(row.tien_nt0 || 0);
        const proportion = rowTienNt / totalTienNt;
        const allocated =
          idx === targets.length - 1 ? remainder : Math.round(Number(totalTax) * proportion * 100) / 100;
        remainder = Math.round((remainder - allocated) * 100) / 100;
        const i = updated.findIndex(x => x.uuid === row.uuid);
        if (i !== -1) updated[i] = { ...updated[i], thue_nt: allocated };
      });

      setDetailRows(updated);
    },
    [detailRows, setDetailRows, thueRows]
  );

  const allocateAllDetailTax = useCallback(
    (totalTax: number) => {
      const totalTienNt = thueRows.reduce((sum: number, r: MuaHangRecord) => sum + Number(r.t_tien_nt || 0), 0);
      if (totalTienNt <= 0 || !Number.isFinite(totalTienNt)) return;

      let remainingTax = Number(totalTax || 0);
      const updated = detailRows.map(r => ({ ...r }));

      updated.forEach((row: MuaHangRecord, index: number) => {
        const rowTienNt = Number(row.tien_nt0 || 0);
        const proportion = rowTienNt / totalTienNt;
        const allocated =
          index === updated.length - 1 ? remainingTax : Math.round(Number(totalTax) * proportion * 100) / 100;
        remainingTax = Math.round((remainingTax - allocated) * 100) / 100;
        row.thue_nt = allocated;
      });

      setDetailRows(updated);
    },
    [detailRows, setDetailRows, thueRows]
  );

  const thueCellValueChange = useCallback(
    (rowUuid: string, field: string, newValue: MuaHangRecord) => {
      if (field === 't_thue_nt' && !originalTaxValuesRef.current[rowUuid]) {
        const thueRow: MuaHangRecord = thueRows.find((r: MuaHangRecord) => r?.uuid === rowUuid) || {};
        originalTaxValuesRef.current[rowUuid] = Number(thueRow?.t_thue_nt || 0);
      }

      handleThueCellValueChange(rowUuid, field, newValue);
    },
    [handleThueCellValueChange, thueRows]
  );

  const thueCellCommit = useCallback(
    (rowUuid: string, field: string, newValue: MuaHangRecord) => {
      if (field === 't_thue_nt') {
        const thueRow: MuaHangRecord = thueRows.find((r: MuaHangRecord) => r?.uuid === rowUuid) || {};
        const originalValue = originalTaxValuesRef.current[rowUuid] || 0;
        const newTaxValue = Number(newValue || 0);

        if (originalValue !== newTaxValue) {
          const maThueUuid = thueRow?.ma_thue_data?.uuid || '';
          setTaxConfirm({ open: true, maThueUuid, newTax: newTaxValue });
        }

        delete originalTaxValuesRef.current[rowUuid];
      }
    },
    [thueRows]
  );

  const documentSelection = useCallback(
    (searchedData: MuaHangRecord) => {
      const { xprocess, customer, selectedRows, dien_giai } = searchedData;
      actions.setCkYn(true);
      actions.setLoaiCk('1');
      if (customer) actions.setKhachHang(customer);
      if (dien_giai) actions.setDienGiai(dien_giai);

      const updatedRows = selectedRows.map((row: MuaHangRecord, index: number) => ({
        uuid: row.uuid,
        line: index + 1,
        ma_vt_data: row.ma_vt_data,
        dvt: row.dvt,
        sl_cl: row.sl_cl,
        so_luong: row.sl_cl,
        gia_nt: row.gia_nt,
        gia_nt0: row.gia_nt,
        tien_nt: roundNumber(row.sl_cl * row.gia_nt),
        tien_nt0: roundNumber(row.sl_cl * row.gia_nt),
        ma_thue_data: row.ma_thue_data,
        thue_suat: row.thue_suat,
        thue_nt: row.thue_nt,
        tk_thue_no_data: row.ma_thue_data?.tk_thue_dau_vao_data,
        ma_bp_data: row.ma_bp_data,
        ma_vv_data: row.ma_vv_data,
        ma_hd_data: row.ma_hd_data,
        ma_dtt_data: row.ma_dtt_data,
        ma_ku_data: row.ma_ku_data,
        ma_phi_data: row.ma_phi_data,
        ma_sp_data: row.ma_sp_data,
        ma_cp0_data: row.ma_cp0_data,
        tk_vt_data: row.ma_vt_data?.tk_khcp_data,
        ma_kho_data: row.ma_kho_data,
        id_dh: row.don_mua_hang_data,
        line_dh: {
          uuid: row.uuid,
          line: row.line
        }
      }));

      setDetailRows(xprocess === '1' ? [...detailRows, ...updatedRows] : updatedRows);
    },
    [actions, detailRows, setDetailRows]
  );

  const allocateDiscount = useCallback(
    (mode: 'quantity' | 'value') => {
      if (!detailRows || detailRows.length === 0) return;

      const totalQty = detailRows.reduce((sum, row) => sum + (Number(row.so_luong) || 0), 0);
      const totalVal = detailRows.reduce((sum, row) => {
        const preDiscount = Number(row.tien_tck_nt) || Number(row.gia_nt0) || Number(row.tien_nt0) || 0;
        return sum + preDiscount;
      }, 0);
      const pool = Number(state.ck_tl_nt) || 0;

      if ((mode === 'quantity' && totalQty === 0) || (mode === 'value' && totalVal === 0)) return;

      setLastDetailAllocationMode(mode);

      const allocatedRows = detailRows.map(row => {
        const preDiscount = Number(row.tien_tck_nt) || Number(row.gia_nt0) || Number(row.tien_nt0) || 0;
        const base = mode === 'quantity' ? Number(row.so_luong) || 0 : preDiscount;
        const denom = mode === 'quantity' ? totalQty : totalVal;
        const ck_nt = Math.round((pool / denom) * base * 100) / 100;
        const tien_nt = Math.max(0, Math.round((preDiscount - ck_nt) * 100) / 100);
        const taxRate = Number(row.ma_thue_data?.thue_suat) || 0;
        const thue_nt = Math.max(0, Math.round(((tien_nt * taxRate) / 100) * 100) / 100);

        return {
          ...row,
          ck_nt,
          tien_tck_nt: row.tien_tck_nt ?? preDiscount,
          thue_nt
        };
      });

      setDetailRows(allocatedRows);
    },
    [detailRows, setDetailRows, state.ck_tl_nt]
  );

  const trackedDetailCellValueChange = useCallback(
    (rowUuid: string, field: string, newValue: MuaHangRecord) => {
      lastEditedDetailFieldRef.current = field === 'gia_nt0' ? 'gia_nt0' : null;
      detailHandleCellValueChange(rowUuid, field, newValue);
    },
    [detailHandleCellValueChange]
  );

  useEffect(() => {
    if (String(state.loai_ck) !== '3') return;
    if (lastEditedDetailFieldRef.current !== 'gia_nt0') return;

    const allocationMode = lastDetailAllocationMode ?? 'value';
    allocateDiscount(allocationMode);
    lastEditedDetailFieldRef.current = null;
  }, [detailRows, state.loai_ck, state.ck_tl_nt, allocateDiscount, lastDetailAllocationMode]);

  const detailFromDetailTab = useCallback(() => {
    const convertedThueRows = buildTaxRowsFromDetailRows(detailRows, thueRows);

    setThueRows(prevRows => {
      const prev: MuaHangRecord[] = prevRows as MuaHangRecord[];
      const newTaxIds = new Set(convertedThueRows.map(getTaxRowIdentity).filter(Boolean));
      const kept = prev.filter(r => {
        const id = getTaxRowIdentity(r);
        return !newTaxIds.has(id);
      });
      return [...kept, ...convertedThueRows].map((row, idx) => ({ ...row, line: idx + 1 }));
    });
  }, [detailRows, setThueRows, thueRows]);

  const confirmTaxAllocation = useCallback(() => {
    if (taxConfirm.maThueUuid && taxConfirm.newTax !== undefined) {
      allocateDetailTax(taxConfirm.maThueUuid, Number(taxConfirm.newTax));
      const totalTax = (thueRows || []).reduce(
        (s: number, r: MuaHangRecord) => s + Number((r as MuaHangRecord).t_thue_nt || 0),
        0
      );
      allocateAllDetailTax(totalTax);
    }
    setTaxConfirm({ open: false });
  }, [allocateAllDetailTax, allocateDetailTax, taxConfirm.maThueUuid, taxConfirm.newTax, thueRows]);

  const initialValues = useMemo(() => {
    if (formMode === 'add' && initialData) {
      return { ...initialData, status: '5' };
    }

    return initialData || initialFormValues;
  }, [formMode, initialData]);

  return {
    formMode,
    initialData,
    initialValues,
    activeTab,
    tabChange: setActiveTab,
    title: getFormTitle(formMode, activeTab),
    actionButtons: getFormActionButtons(formMode, activeTab, { onAdd, onEdit, onDelete, onCopy, onClose }),
    schema,
    constraints,
    formState,
    onClose,
    submit,
    totals,
    detail: {
      rows: detailRows,
      selectedRowUuid: detailSelectedRowUuid,
      selectedCell: detailSelectedCell,
      handleRowClick: detailHandleRowClick,
      handleCellClick: detailHandleCellClick,
      handleAddRow: detailHandleAddRow,
      handleDeleteRow: detailHandleDeleteRow,
      handleCopyRow: detailHandleCopyRow,
      handlePasteRow: detailHandlePasteRow,
      handleMoveRow: detailHandleMoveRow,
      allocateDiscount,
      trackedCellValueChange: trackedDetailCellValueChange,
      repeatRows: repeatDetailRows,
      setNonWritableFields,
      setPasteConfigs,
      isPasting
    },
    cost: {
      rows: chiPhiRows,
      selectedRowUuid: chiPhiSelectedRowUuid,
      selectedCell: chiPhiSelectedCell,
      handleRowClick: handleChiPhiRowClick,
      handleCellClick: handleChiPhiCellClick,
      handleAddRow: handleAddChiPhiRow,
      handleDeleteRow: handleDeleteChiPhiRow,
      handleCopyRow: handleCopyChiPhiRow,
      handlePasteRow: handlePasteChiPhiRow,
      handleMoveRow: handleMoveChiPhiRow,
      handleCellValueChange: handleChiPhiCellValueChange,
      setNonWritableFields: setChiPhiNonWritableFields,
      setPasteConfigs: setChiPhiPasteConfigs,
      isPasting: chiPhiIsPasting
    },
    costDetail: {
      rows: chiPhiChiTietRows,
      selectedRowUuid: chiPhiChiTietSelectedRowUuid,
      handleRowClick: handleChiPhiChiTietRowClick,
      handleAddRow: handleAddChiPhiChiTietRow,
      handleDeleteRow: handleDeleteChiPhiChiTietRow,
      handleCopyRow: handleCopyChiPhiChiTietRow,
      handlePasteRow: handlePasteChiPhiChiTietRow,
      handleMoveRow: handleMoveChiPhiChiTietRow,
      handleCellValueChange: handleChiPhiChiTietCellValueChange
    },
    tax: {
      rows: thueRows,
      selectedRowUuid: thueSelectedRowUuid,
      selectedCell: thueSelectedCell,
      handleRowClick: handleThueRowClick,
      handleCellClick: handleThueCellClick,
      handleAddRow: handleAddThueRow,
      handleDeleteRow: handleDeleteThueRow,
      handleCopyRow: handleCopyThueRow,
      handlePasteRow: handlePasteThueRow,
      handleMoveRow: handleMoveThueRow,
      cellValueChange: thueCellValueChange,
      cellCommit: thueCellCommit,
      setNonWritableFields: setThueNonWritableFields,
      setPasteConfigs: setThuePasteConfigs,
      detailFromDetailTab,
      isPasting: thueIsPasting
    },
    taxConfirm: {
      open: taxConfirm.open,
      close: () => setTaxConfirm({ open: false }),
      confirm: confirmTaxAllocation
    },
    documents: {
      open: documentsOpen,
      show: () => setDocumentsOpen(true),
      close: () => setDocumentsOpen(false),
      selection: documentSelection,
      columns: getDocumentsColumns()
    }
  };
};

export type FormDialogModel = ReturnType<typeof useFormDialog>;
